import { useEffect, useMemo, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import Cookbook from "./Cookbook";
import RecipeBook from "./RecipeBook";
import NutritionDashboard from "./NutritionDashboard";
import GateCard from "./GateCard";
import GatePicker from "./GatePicker";
import FlairHalo, { isFlairActive } from "./FlairHalo";
import SchedulePicker from "./SchedulePicker";
import { useUserProfile } from "../lib/useUserProfile";
import { useBadges } from "../lib/useBadges";
import { useScheduledMeals } from "../lib/useScheduledMeals";
import { SKILL_TREE, DIETARY_OPTIONS, LEVEL_OPTIONS, GOAL_OPTIONS } from "../data";
import { skillIconFor } from "../lib/canonicalIcons";

// Rating meta — kept in sync with Cookbook's table so the recent-cooks
// strip renders with the same face language as the cookbook itself.
const RATING_META = {
  nailed: { emoji: "🤩", color: COLOR.gold },
  good:   { emoji: "😊", color: COLOR.leaf },
  meh:    { emoji: "😐", color: COLOR.dim    },
  rough:  { emoji: "😬", color: COLOR.rose },
};

function relativeDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

// First-letter avatar in the mise palette. Deterministic per name so a
// family member's initial stays the same color every time you see it.
function avatarColor(name) {
  if (!name) return COLOR.goldDeep;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const palette = [COLOR.goldDeep, "#15201a", "#1a1228", "#2a1a15", COLOR.skyDeep, "#2a1e28"];
  return palette[Math.abs(hash) % palette.length];
}

/**
 * Full-screen overlay showing another user's cooking profile. Shown the
 * same way Settings is — fixed 480px shell, × to close.
 *
 * Props:
 *   targetUserId   — whose profile to show
 *   viewerId       — the signed-in user's id (for "our history together"
 *                    + to detect self-view which grays the relationship
 *                    chip)
 *   relationship   — "family" | "friend" | "self" | "stranger". Drives
 *                    the banner chip and what the empty states say. The
 *                    caller already knows this from useRelationships, so
 *                    passing it in saves a query here.
 *   nameFor        — id → string, used for diner attributions on the
 *                    shared-cooks strip
 *   onOpenCook     — (cookLogId) => void — tap a cook card to open it in
 *                    Cookbook (deep-link through the existing pipeline)
 *   onClose
 */
export default function UserProfile({
  targetUserId, viewerId, relationship = "stranger",
  familyKey, nameFor, onOpenCook, onOpenProfile, onClose,
  // Self-view only — opens the Settings / Notifications overlays.
  // Surfaced here because the fixed top-bar icons were pulled; the
  // profile header is now the single entry point for both.
  onOpenSettings,
  // Self-view only — opens the new SocialPage (relationships, invite
  // code, find friends). Carved out of Settings 2026-05-11; if not
  // provided the PEOPLE button is hidden.
  onOpenSocial,
  // Cook-log deep link handed in from App (notification tap / Home
  // feed). When present, we auto-open the full Cookbook overlay so the
  // embedded Cookbook's own pipeline resolves the detail view.
  deepLink, onConsumeDeepLink,
  // Recipe Book wiring. onStartCook fires CookMode against a recipe
  // object (the saved recipe.recipe payload). hasFamily gates the
  // SHARE pill on each spread. Both optional — the Recipe Book entry
  // is hidden when onStartCook isn't provided (read-only profile).
  // `family` is the [{ otherId, other:{name} }] list used by
  // SchedulePicker's REASSIGN row; passed in from App so we don't
  // re-mount the relationships hook here.
  onStartCook, hasFamily = false, family = [], friends = [],
}) {
  const { profile, cooks, stats, sharedCooks, loading, error, setNutritionTargets } =
    useUserProfile(targetUserId, viewerId);

  // "VIEW FULL COOKBOOK" — opens the existing Cookbook component as a
  // modal overlay. Cookbook ships with scope / filter / search / detail
  // built in, so folding it in here gives UserProfile the full archive
  // surface without duplicating that logic. Limited to self + family
  // since RLS only returns cook_logs for those scopes anyway.
  const [showCookbook, setShowCookbook] = useState(false);
  // Recipe Book overlay (saved-recipe library — distinct from Cookbook
  // which is the cook-log archive). Self-view only; the book is the
  // viewer's own user_recipes rows, served via the dedicated
  // useRecipeBook hook (clean-room of useUserRecipes — see header
  // comment in src/lib/useRecipeBook.js).
  const [showRecipeBook, setShowRecipeBook] = useState(false);
  // Ranked-match picker state; opened from GateCard when all
  // prereqs are green.
  const [pickerState, setPickerState] = useState(null);
  // SchedulePicker state — set when the Recipe Book's SCHEDULE button
  // fires; cleared on save / dismiss.
  const [schedulingRecipe, setSchedulingRecipe] = useState(null);
  // Scheduling writer. Only mounted for self-view; family/friend
  // profiles can't schedule on someone else's calendar.
  const { schedule } = useScheduledMeals(viewerId);
  // Broken-image fallback for the large identity avatar. Google
  // avatar URLs sometimes 403 after rotation.
  const [avatarBroken, setAvatarBroken] = useState(false);

  // Auto-open the Cookbook overlay whenever a cook_log deep-link lands.
  // The embedded Cookbook then consumes the same deepLink prop and
  // opens the detail view. Only self-view can show the archive
  // (family-scoped cook_logs only come back for the viewer's own id),
  // so we gate on that to avoid opening an empty overlay.
  useEffect(() => {
    if (deepLink?.kind === "cook_log" && viewerId === targetUserId) {
      setShowCookbook(true);
    }
  }, [deepLink, viewerId, targetUserId]);

  const isSelf = relationship === "self" || (viewerId && viewerId === targetUserId);
  const name   = profile?.name || (isSelf ? "You" : "Someone");
  const first  = name.split(/\s+/)[0];
  const initial = name[0]?.toUpperCase() || "?";

  const skills = useMemo(() => {
    const lvls = profile?.skill_levels || {};
    return SKILL_TREE.map(s => ({ ...s, level: lvls[s.id] ?? 0 }));
  }, [profile]);
  const hasAnySkill = skills.some(s => s.level > 0);

  const diet = DIETARY_OPTIONS.find(d => d.id === profile?.dietary);
  const level = LEVEL_OPTIONS.find(l => l.id === profile?.skill_self_report);
  const goal  = GOAL_OPTIONS.find(g => g.id === profile?.goal);

  // Badges — earned (colored) vs catalog-leftover (silhouette) so the
  // empty slots double as "here's what's out there to unlock" without
  // spoiling anything.
  const badges = useBadges(targetUserId);

  // When the viewer is a FRIEND (not family), RLS returns the profile row
  // but cook_logs is empty — that's by design (friends share prefs only).
  // We surface a soft note rather than leave an awkward empty stats block.
  const friendsLimited = relationship === "friend" && !isSelf;

  // Hidden = we got nothing back. Either stranger access or a deleted
  // account. Show a graceful empty panel with close.
  const hidden = !loading && !profile && !isSelf;

  return (
    <div style={{ position:"fixed", inset:0, background:COLOR.night, zIndex:200, maxWidth:480, margin:"0 auto", overflowY:"auto" }}>
      <div style={{ padding:"calc(20px + env(safe-area-inset-top)) 20px calc(80px + env(safe-area-inset-bottom))" }}>
        {/* Header — back-to-home button (left) + label, settings
            (self only) on the right. Close × moved to a proper back
            arrow so the exit affordance reads as navigation, not
            dismiss; settings takes the right slot the × used to hold. */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <button
              onClick={onClose}
              title="Back to home"
              aria-label="Back to home"
              style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:20, width:36, height:36, color:COLOR.dim, fontSize:18, cursor:"pointer", lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}
            >
              ←
            </button>
            <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.12em" }}>
              {isSelf ? "YOUR PROFILE" : "CHEF PROFILE"}
            </div>
          </div>
          {isSelf && (onOpenSettings || onOpenSocial) && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {onOpenSocial && (
                <button
                  onClick={onOpenSocial}
                  title="Family & Friends"
                  aria-label="Family & Friends"
                  style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:20, height:36, padding:"0 12px", color:COLOR.gold, fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.14em", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}
                >
                  👥 PEOPLE
                </button>
              )}
              {onOpenSettings && (
                <button
                  onClick={onOpenSettings}
                  title="Settings"
                  aria-label="Settings"
                  style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:20, width:36, height:36, color:COLOR.dim, fontSize:18, cursor:"pointer", lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}
                >
                  ⚙
                </button>
              )}
            </div>
          )}
        </div>

        {hidden ? (
          <div style={{ marginTop:60, textAlign:"center", padding:"40px 20px", background:COLOR.deep, border:`1px dashed ${COLOR.edge}`, borderRadius:18 }}>
            <div style={{ fontSize:48, marginBottom:12, opacity:0.5 }}>🫥</div>
            <div style={{ fontFamily:FONT.serif, fontSize:20, fontStyle:"italic", color:COLOR.dim, marginBottom:6 }}>
              Profile unavailable
            </div>
            <div style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted }}>
              Either this person hasn't accepted a connection with you yet, or the account has been removed.
            </div>
          </div>
        ) : loading && !profile ? (
          <div style={{ marginTop:60, textAlign:"center", color:COLOR.muted, fontFamily:FONT.sans, fontSize:13 }}>
            Loading…
          </div>
        ) : (
          <>
            {/* Identity block — avatar, name, relationship. Self view
                gets the FlairHalo when the user's daily roll awarded
                an avatar_sparkle cosmetic within the flair_hours window. */}
            <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
              <FlairHalo active={isSelf && isFlairActive(profile)} size={72}>
                {profile?.avatar_url && !avatarBroken ? (
                  <img
                    src={profile.avatar_url}
                    alt={name}
                    onError={() => setAvatarBroken(true)}
                    style={{ width:72, height:72, borderRadius:36, objectFit:"cover", flexShrink:0, display:"block", background:COLOR.soil }}
                  />
                ) : (
                  <div style={{ width:72, height:72, borderRadius:36, background: avatarColor(name), color:COLOR.gold, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT.serif, fontSize:34, fontWeight:500, flexShrink:0 }}>
                    {initial}
                  </div>
                )}
              </FlairHalo>
              <div style={{ flex:1, minWidth:0 }}>
                <h1 style={{ fontFamily:FONT.itemTitle, fontSize:28, fontWeight:500, fontStyle:"italic", color:COLOR.ink, margin:0, letterSpacing:"-0.02em" }}>
                  {name}
                </h1>
                {profile?.username && (
                  <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.muted, letterSpacing:"0.04em", marginTop:4 }}>
                    @{profile.username}
                  </div>
                )}
                <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                  {isSelf && <Chip bg={COLOR.goldDeep} color={COLOR.goldDim} text={COLOR.gold}>YOU</Chip>}
                  {!isSelf && relationship === "family" && <Chip bg="#1a2015" color={COLOR.leafDim} text={COLOR.leafMuted}>FAMILY</Chip>}
                  {!isSelf && relationship === "friend" && <Chip bg={COLOR.skyDeep} color="#1e2a3a" text="#77a3d9">FRIEND</Chip>}
                  {diet && <Chip>{diet.label.toUpperCase()}</Chip>}
                </div>
                {(level || goal) && (
                  <div style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, marginTop:6 }}>
                    {[level?.label, goal?.label].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>

            {/* Level band — numeric level + title + progress bar to
                next. Curve and titles match xp_config / xp_level_titles
                so the display is correct until product retunes. */}
            <LevelBand level={profile?.level || 1} totalXp={profile?.total_xp || 0} />

            {/* Recipe Book — top-of-profile hero. Self-view only. Full-
                bleed banner using /img/recipe-book.png as the artwork;
                copy overlays bottom-left with a subtle gradient for
                legibility. Mirrors the Shop Mode banner pattern in
                MCMShoppingScreen so the two key destinations read as
                the same affordance language. */}
            {isSelf && onStartCook && (
              <button
                type="button"
                onClick={() => setShowRecipeBook(true)}
                aria-label="Open your recipe book"
                style={{
                  display:"block",
                  width:"100%",
                  marginBottom:20,
                  padding:0,
                  border:`1px solid rgba(155,116,66,0.35)`,
                  borderRadius:14,
                  overflow:"hidden",
                  position:"relative",
                  cursor:"pointer",
                  backgroundColor:COLOR.soil,
                  backgroundImage:"url(/img/recipe-book.png)",
                  backgroundSize:"cover",
                  backgroundPosition:"center",
                  backgroundRepeat:"no-repeat",
                  aspectRatio:"16 / 7",
                  minHeight:160,
                  textAlign:"left",
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position:"absolute",
                    inset:0,
                    background:"linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
                    pointerEvents:"none",
                  }}
                />
                <div style={{
                  position:"absolute",
                  left:18,
                  right:18,
                  bottom:16,
                  display:"flex",
                  flexDirection:"column",
                  gap:0,
                }}>
                  <div style={{
                    fontFamily:FONT.itemTitle,
                    fontSize:22,
                    fontWeight:500,
                    fontStyle:"italic",
                    lineHeight:1.05,
                    color:"#fff",
                    textShadow:"0 2px 8px rgba(0,0,0,0.45)",
                  }}>
                    Your Recipe Book
                  </div>
                  <div style={{
                    fontFamily:FONT.forma,
                    fontSize:13,
                    lineHeight:1.3,
                    color:"rgba(255,255,255,0.92)",
                    textShadow:"0 1px 4px rgba(0,0,0,0.5)",
                    marginTop:2,
                  }}>
                    Custom builds and AI drafts, gathered in one paged volume.
                  </div>
                </div>
              </button>
            )}

            {/* Gate card — visible only when the user has an active
                (non-passed) user_gate_progress row. Self-only: a
                family member shouldn't see your in-progress
                ranked-match until you pass it. */}
            {isSelf && (
              <GateCard
                userId={viewerId}
                onOpenPicker={(gate, progress) => setPickerState({ gate, progress })}
              />
            )}


            {/* Nutrition dashboard — per-user tally of calories/protein/
                fat/carbs with day / week / month views and a goal editor.
                Self-only by design: another user's macros are private. */}
            {isSelf && (
              <NutritionDashboard
                userId={viewerId}
                targets={profile?.nutrition_targets}
                onUpdateTargets={setNutritionTargets}
              />
            )}

            {/* Badge wall. Earned badges render in full color (SVG from
                public/badges/), locked badges render as dim silhouettes
                so the user sees what's out there without spoiling the
                exact trigger. Always rendered so an empty catalog
                surfaces a visible state rather than silently hiding
                itself (the previous behavior made a missing migration
                indistinguishable from "no badges yet"). */}
            <Section label={`BADGES · ${badges.earnedList.length}/${badges.earnedList.length + badges.lockedList.length}`}>
              {badges.loading ? (
                <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted, fontStyle:"italic", margin:0 }}>
                  Loading…
                </p>
              ) : badges.earnedList.length + badges.lockedList.length === 0 ? (
                <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted, fontStyle:"italic", margin:0, lineHeight:1.6 }}>
                  No badges in the catalog yet. {isSelf ? "If you expected one here, make sure the latest migrations are applied." : ""}
                </p>
              ) : (
                <BadgeWall
                  earned={badges.earnedList}
                  locked={badges.lockedList}
                  isSelf={isSelf}
                  firstName={first}
                />
              )}
            </Section>

            {/* Our history together — only when viewer ate at one+ of
                the target's cooks. Quick recall: "we ate this together". */}
            {!isSelf && sharedCooks.length > 0 && (
              <Section label={`YOU'VE EATEN ${sharedCooks.length} OF THEIR MEALS`}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {sharedCooks.slice(0, 4).map(c => (
                    <CookRow key={c.id} cook={c} onOpen={onOpenCook} />
                  ))}
                </div>
              </Section>
            )}

            {/* Recent cooks — limited for friends by RLS, so we show a
                gentle note instead of pretending they've never cooked. */}
            {friendsLimited ? (
              <Section label="RECENT COOKS">
                <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted, fontStyle:"italic", margin:0, lineHeight:1.6 }}>
                  Friends only share dietary preferences. Upgrade to family in Settings to see each other's cookbooks and meal plans.
                </p>
              </Section>
            ) : cooks.length > 0 ? (
              <Section label={isSelf ? "YOUR RECENT COOKS" : "RECENT COOKS"}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {cooks.slice(0, 8).map(c => (
                    <CookRow key={c.id} cook={c} onOpen={onOpenCook} />
                  ))}
                </div>
                {(isSelf || relationship === "family") && cooks.length >= 1 && (
                  <button
                    onClick={() => setShowCookbook(true)}
                    style={{
                      marginTop:10, width:"100%", padding:"12px",
                      background:COLOR.soil, border:`1px solid ${COLOR.border}`,
                      color:COLOR.gold, borderRadius:12,
                      fontFamily:FONT.mono, fontSize:11, fontWeight:600,
                      letterSpacing:"0.1em", cursor:"pointer",
                    }}
                  >
                    VIEW FULL COOKBOOK →
                  </button>
                )}
              </Section>
            ) : (
              <Section label={isSelf ? "YOUR RECENT COOKS" : "RECENT COOKS"}>
                <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.sub, fontStyle:"italic", margin:0 }}>
                  {isSelf
                    ? "Finish a meal in Cook Mode to start your cookbook."
                    : "Nothing cooked yet."}
                </p>
              </Section>
            )}

            {/* Skill tree. Shown whenever there are any skill rows,
                even if all are level 0 for the target — the visual
                still reads "here's where they could grow". For friends
                the profile row is visible (RLS) but skill_levels stays
                empty unless the target has filled it in themselves. */}
            <Section label={isSelf ? "YOUR SKILL TREE" : "SKILLS"}>
              {hasAnySkill || isSelf ? (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {skills.map(s => {
                    const iconUrl = skillIconFor(s.id);
                    return (
                    <div key={s.id} style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, padding:"10px 14px", display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:40, height:40, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {iconUrl ? (
                          <img src={iconUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"contain" }} />
                        ) : (
                          <div style={{ fontSize:22 }}>{s.emoji}</div>
                        )}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <div style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.ink }}>{s.name}</div>
                          <div style={{ fontFamily:FONT.mono, fontSize:10, color: s.level > 0 ? s.color : COLOR.sub }}>
                            LVL {s.level}/{s.maxLevel}
                          </div>
                        </div>
                        <div style={{ height:3, background:COLOR.edge, borderRadius:2, overflow:"hidden" }}>
                          <div style={{ height:"100%", borderRadius:2, background: s.color, width:`${(s.level/s.maxLevel)*100}%`, boxShadow: s.level > 0 ? `0 0 6px ${s.color}88` : "none", transition:"width 0.3s" }} />
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.sub, fontStyle:"italic", margin:0 }}>
                  No skills leveled up yet.
                </p>
              )}
            </Section>

            {/* Favorite cuisine readout — cheap signal but it's the sort of
                line that reads like "they'd love my ramen pop-up". */}
            {stats.favCuisine && (
              <Section label="FAVORITE CUISINE">
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12 }}>
                  <div style={{ fontSize:24 }}>🌍</div>
                  <div style={{ fontFamily:FONT.serif, fontSize:18, color:COLOR.ink, textTransform:"capitalize" }}>
                    {stats.favCuisine}
                  </div>
                  {stats.firstCookedAt && (
                    <div style={{ marginLeft:"auto", fontFamily:FONT.mono, fontSize:10, color:COLOR.sub, letterSpacing:"0.08em" }}>
                      SINCE {relativeDate(stats.firstCookedAt).toUpperCase()}
                    </div>
                  )}
                </div>
              </Section>
            )}

            {error && (
              <div style={{ marginTop:24, padding:"10px 12px", background:COLOR.roseDeep, border:"1px solid #3a1e1e", color:COLOR.rose, borderRadius:8, fontFamily:FONT.sans, fontSize:12 }}>
                Couldn't fully load {first}'s profile.
              </div>
            )}
          </>
        )}
      </div>

      {/* Full-cookbook overlay — launched from VIEW FULL COOKBOOK.
          Cookbook renders its own list + filters + detail, so we just
          host the component inside a fixed overlay and provide a back
          button. Familyscope cook_logs reads self+family, so the list
          will mirror what the viewer sees in the feed. */}
      {showCookbook && (
        <div style={{ position:"fixed", inset:0, background:COLOR.night, zIndex:220, maxWidth:480, margin:"0 auto", overflowY:"auto" }}>
          {/* Cookbook owns its own sticky header chrome (list bar +
              detail bar), including the safe-area-inset-top padding —
              rendering a second bar here produced stacked back buttons
              and pushed the reachable one under the status bar. */}
          <Cookbook
            userId={viewerId}
            familyKey={familyKey}
            nameFor={nameFor}
            deepLink={deepLink}
            onConsumeDeepLink={onConsumeDeepLink || (() => {})}
            onOpenProfile={onOpenProfile}
            onClose={() => setShowCookbook(false)}
          />
        </div>
      )}

      {/* Recipe Book overlay. Self-view only (the entry button gates
          on isSelf + onStartCook). Closes itself before forwarding to
          onStartCook so CookMode lands on top of a clean stack. */}
      {showRecipeBook && (
        <RecipeBook
          open={showRecipeBook}
          onClose={() => setShowRecipeBook(false)}
          userId={viewerId}
          viewerName={profile?.name?.split(/\s+/)[0] || (nameFor && nameFor(viewerId)) || ""}
          hasFamily={hasFamily}
          // SAFETY: this mount is self-view only, so the loaded
          // profile IS the viewer — thread their allergies or this
          // surface skips the CONTAINS-X chips and suggest-pairing
          // exclusions the App-level mount has (and the un-excluded
          // pairing result would be cached onto the recipe row).
          viewerAllergies={Array.isArray(profile?.allergies) ? profile.allergies : []}
          onCook={(recipe) => {
            setShowRecipeBook(false);
            onStartCook?.(recipe);
          }}
          onSchedule={(recipe) => {
            // Don't close the Recipe Book — SchedulePicker stacks on
            // top via a portal so the user lands back on the same
            // recipe page after they save / cancel.
            setSchedulingRecipe(recipe);
          }}
        />
      )}
      {schedulingRecipe && (
        <SchedulePicker
          recipe={schedulingRecipe}
          userId={viewerId}
          userName={profile?.name?.split(/\s+/)[0] || ""}
          family={family}
          friends={friends}
          defaultRequest={false}
          onClose={() => setSchedulingRecipe(null)}
          onSave={async ({ scheduledFor, notificationSettings, note, cookId, isRequest, servings, mealSlot, eatAnchor }) => {
            await schedule({
              recipeSlug: schedulingRecipe.slug,
              recipeUserId: schedulingRecipe._userId || viewerId,
              scheduledFor,
              notificationSettings,
              note,
              cookId,
              isRequest,
              servings,
              mealSlot,
              eatAnchor,
            });
          }}
        />
      )}

      {pickerState && (
        <GatePicker
          userId={viewerId}
          gate={pickerState.gate}
          progress={pickerState.progress}
          onClose={() => setPickerState(null)}
          onPicked={() => setPickerState(null)}
        />
      )}
    </div>
  );
}

// ── Tiny shared UI bits ──────────────────────────────────────────────────────

function Section({ label, children }) {
  return (
    <div style={{ marginTop:18 }}>
      <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.12em", marginBottom:10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Chip({ color = COLOR.border, bg = COLOR.soil, text = COLOR.dim, children }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", background: bg, border:`1px solid ${color}`, color: text, borderRadius:20, padding:"3px 9px", fontFamily:FONT.mono, fontSize:9, letterSpacing:"0.08em" }}>
      {children}
    </span>
  );
}

// Numeric level + title + XP-to-next progress bar. Pure client math
// mirrors xp_to_next(L) = round(100 * L^1.6) from 0102. Source of
// truth is xp_config / xp_level_titles; if product retunes the curve
// the DB stays authoritative and this component re-renders against
// the user's recomputed profile.level.
const LEVEL_TITLES = [
  { min: 1,  max: 5,   title: "Apprentice" },
  { min: 6,  max: 10,  title: "Line Cook" },
  { min: 11, max: 20,  title: "Home Chef" },
  { min: 21, max: 35,  title: "Sous Chef" },
  { min: 36, max: 50,  title: "Head Chef" },
  { min: 51, max: 75,  title: "Executive Chef" },
  { min: 76, max: 999, title: "Iron Chef" },
];
function titleForLevel(L) {
  const row = LEVEL_TITLES.find(r => L >= r.min && L <= r.max);
  return row?.title || "Apprentice";
}
function xpToNext(L) {
  return Math.max(1, Math.round(100 * Math.pow(L, 1.6)));
}
function xpInLevel(totalXp, L) {
  // Sum cumulative xp_to_next(1..L-1) and subtract from totalXp.
  let sum = 0;
  for (let i = 1; i < L; i++) sum += xpToNext(i);
  return Math.max(0, totalXp - sum);
}

function LevelBand({ level, totalXp }) {
  const title = titleForLevel(level);
  const inLevel = xpInLevel(totalXp, level);
  const toNext = xpToNext(level);
  const pct = Math.min(100, (inLevel / toNext) * 100);

  return (
    <div style={{
      background: COLOR.soil, border: `1px solid ${COLOR.border}`, borderRadius: 12,
      padding: "12px 14px", marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <span style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.muted, letterSpacing: "0.08em" }}>
            L
          </span>
          <span style={{ fontFamily: FONT.serif, fontSize: 28, fontWeight: 500, color: COLOR.gold, marginLeft: 2 }}>
            {level}
          </span>
          <span style={{ fontFamily: FONT.serif, fontSize: 15, fontStyle: "italic", color: COLOR.ink, marginLeft: 10 }}>
            {title}
          </span>
        </div>
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
          {inLevel.toLocaleString()} / {toNext.toLocaleString()}
        </div>
      </div>
      <div style={{ background: COLOR.deep, borderRadius: 6, height: 8, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "linear-gradient(90deg, #e07a3a, #f5c842)",
          transition: "width 400ms ease-out",
        }} />
      </div>
    </div>
  );
}

// Badge wall — 4-up grid mixing earned (full color) and locked
// (silhouetted) tiles. Tapping any tile opens a small detail card with
// the description + earn rule so the user knows what to do next. Locked
// badges still show the silhouette so the collection feels discoverable
// rather than empty.
function BadgeWall({ earned, locked, isSelf, firstName }) {
  const [detail, setDetail] = useState(null);
  const all = [
    ...earned.map(b => ({ ...b, isEarned: true })),
    ...locked.map(b => ({ ...b, isEarned: false })),
  ];
  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:6 }}>
        {all.map(b => {
          // Little corner marker when the earned badge is scarce. 1/1
          // gets a filled 🥇; small-cap gets the cap number itself.
          const rareChip = b.isEarned && typeof b.maxAwards === "number" && b.maxAwards <= 10
            ? (b.maxAwards === 1 ? "🥇" : `1/${b.maxAwards}`)
            : null;
          return (
            <button
              key={b.id}
              onClick={() => setDetail(b)}
              title={b.isEarned ? `${b.name} — earned` : `${b.name} — locked`}
              style={{
                position: "relative",
                aspectRatio: "1 / 1",
                background: b.isEarned ? COLOR.goldDeep : COLOR.deep,
                border: `1px solid ${b.isEarned ? b.color + "55" : COLOR.edge}`,
                borderRadius: 12, padding: 3,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                // Lock styling: desaturate + dim so the silhouette reads
                // "not yet" without hiding the shape of the icon.
                filter: b.isEarned ? "none" : "grayscale(1) brightness(0.35)",
                opacity: b.isEarned ? 1 : 0.7,
                transition: "all 0.2s",
                // Subtle pulse-glow for earned 1/1s so the wall makes
                // them obviously special.
                boxShadow: b.isEarned && b.maxAwards === 1
                  ? `0 0 18px ${b.color}55`
                  : "none",
              }}
            >
              <img
                src={b.iconPath}
                alt={b.name}
                style={{ width: "94%", height: "94%", objectFit: "contain", pointerEvents: "none" }}
                onError={(e) => {
                  e.currentTarget.outerHTML =
                    '<div style="font-size:28px;color:' + (b.isEarned ? b.color : COLOR.sub) + '">🏅</div>';
                }}
              />
              {rareChip && (
                <span style={{
                  position: "absolute", bottom: 4, right: 4,
                  fontFamily: FONT.mono, fontSize: 9,
                  color: COLOR.night, background: b.color,
                  padding: "1px 5px", borderRadius: 10,
                  fontWeight: 700, letterSpacing: "0.04em",
                }}>
                  {rareChip}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!isSelf && earned.length === 0 && locked.length > 0 && (
        <p style={{ marginTop:10, fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, fontStyle:"italic", margin:"10px 0 0" }}>
          {firstName} hasn't earned any badges yet.
        </p>
      )}
      {isSelf && earned.length === 0 && locked.length > 0 && (
        <p style={{ marginTop:10, fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, fontStyle:"italic", margin:"10px 0 0" }}>
          Cook a badge-eligible recipe and rate it good or nailed to unlock your first one.
        </p>
      )}

      {detail && (
        <BadgeDetail badge={detail} onClose={() => setDetail(null)} isSelf={isSelf} />
      )}
    </>
  );
}

// Render the "rarity" chip that sits above the badge name — turns
// max_awards into language. 1/1 gets gold; small N (<=10) gets a
// bronze/silver vibe; null = standard (no chip).
function rarityChip(badge) {
  if (badge.maxAwards === 1) {
    return { label: "1 OF 1 · FOREVER", color: COLOR.gold, bg: COLOR.goldDeep };
  }
  if (typeof badge.maxAwards === "number" && badge.maxAwards > 1 && badge.maxAwards <= 10) {
    return { label: `1 OF ${badge.maxAwards}`, color: COLOR.gold, bg: "#18150a" };
  }
  return null;
}

function BadgeDetail({ badge, onClose, isSelf }) {
  const earned = !!badge.earnedAt;
  const rarity = rarityChip(badge);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, cursor: "zoom-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 360, background: COLOR.ground,
          border: `1px solid ${earned ? badge.color + "66" : COLOR.border}`,
          borderRadius: 18, padding: "24px", cursor: "default",
          textAlign: "center",
          // Rare earned badges get a more dramatic glow; standards stay soft.
          boxShadow: earned
            ? (rarity ? `0 0 72px ${badge.color}55` : `0 0 48px ${badge.color}22`)
            : "0 12px 40px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{
          width: 128, height: 128, margin: "0 auto 16px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: earned ? COLOR.goldDeep : COLOR.deep,
          border: `1px solid ${earned ? badge.color + "66" : COLOR.border}`,
          borderRadius: 20,
          filter: earned ? "none" : "grayscale(1) brightness(0.4)",
        }}>
          <img
            src={badge.iconPath}
            alt={badge.name}
            style={{ width: "80%", height: "80%", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.outerHTML =
                '<div style="font-size:64px;color:' + (earned ? badge.color : COLOR.sub) + '">🏅</div>';
            }}
          />
        </div>

        {/* Rarity chip — only shown when the badge is actually scarce.
            Standard badges read cleaner without one. */}
        {rarity && (
          <div style={{ display:"inline-block", padding:"4px 10px", borderRadius:20, background:rarity.bg, border:`1px solid ${rarity.color}66`, marginBottom:8 }}>
            <span style={{ fontFamily:FONT.mono, fontSize:10, color:rarity.color, letterSpacing:"0.14em", fontWeight:600 }}>
              🥇 {rarity.label}
            </span>
          </div>
        )}

        <div style={{ fontFamily:FONT.mono, fontSize:10, color: earned ? badge.color : COLOR.muted, letterSpacing:"0.14em", marginBottom:4 }}>
          {earned ? "EARNED" : "LOCKED"}
        </div>
        <h3 style={{ fontFamily:FONT.serif, fontSize:22, fontStyle:"italic", color:COLOR.ink, fontWeight:300, margin:"0 0 6px" }}>
          {badge.name}
        </h3>
        <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.dim, lineHeight:1.6, margin:"0 0 14px" }}>
          {badge.description}
        </p>

        {/* The earn_reason — the specific "why" this earner got this
            badge. Populated by the award trigger at INSERT time. Only
            shows for earned badges; locked viewers see the earn_rule
            block below instead. */}
        {earned && badge.earnReason && (
          <div style={{ padding:"12px 14px", background:COLOR.goldDeep, border:`1px solid ${badge.color}44`, borderRadius:10, marginBottom:12 }}>
            <div style={{ fontFamily:FONT.mono, fontSize:9, color:badge.color, letterSpacing:"0.12em", marginBottom:4 }}>
              WHY {isSelf ? "YOU" : "THEY"} EARNED IT
            </div>
            <div style={{ fontFamily:FONT.serif, fontStyle:"italic", fontSize:14, color:COLOR.ink, lineHeight:1.5 }}>
              "{badge.earnReason}"
            </div>
          </div>
        )}

        {!earned && badge.earnRule && (
          <div style={{ padding:"10px 14px", background:COLOR.deep, border:`1px solid ${COLOR.edge}`, borderRadius:10 }}>
            <div style={{ fontFamily:FONT.mono, fontSize:9, color:COLOR.muted, letterSpacing:"0.12em", marginBottom:4 }}>
              HOW TO EARN
            </div>
            <div style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.dim, lineHeight:1.5 }}>
              {badge.earnRule}
            </div>
          </div>
        )}

        {earned && badge.earnedAt && (
          <div style={{ marginTop:12, fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, letterSpacing:"0.1em" }}>
            {isSelf ? "YOU EARNED IT" : "EARNED"} · {new Date(badge.earnedAt).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}
          </div>
        )}
        <button
          onClick={onClose}
          style={{
            marginTop:18, width:"100%", padding:"12px",
            background:COLOR.soil, color:COLOR.dim,
            border:`1px solid ${COLOR.border}`, borderRadius:10,
            fontFamily:FONT.mono, fontSize:11, letterSpacing:"0.08em",
            cursor:"pointer",
          }}
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}

// Compact one-line recipe row. Tapping routes back into the Cookbook
// detail via the existing deep-link pipeline, so reviews/photos/etc
// all come for free.
function CookRow({ cook, onOpen }) {
  const m = RATING_META[cook.rating] || RATING_META.meh;
  return (
    <button
      onClick={() => onOpen?.(cook.id)}
      style={{ display:"flex", alignItems:"center", gap:12, background:COLOR.ground, border:`1px solid ${COLOR.edge}`, borderRadius:12, padding:"10px 14px", cursor:"pointer", textAlign:"left", width:"100%" }}
    >
      <div style={{ fontSize:24, flexShrink:0 }}>{cook.recipeEmoji}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {cook.recipeTitle}
        </div>
        <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, marginTop:2, display:"flex", gap:6, alignItems:"center" }}>
          <span>{m.emoji}</span>
          <span>{relativeDate(cook.cookedAt)}</span>
          <span style={{ color:COLOR.sub }}>·</span>
          <span>+{cook.xpEarned} XP</span>
        </div>
      </div>
      <span style={{ fontFamily:FONT.mono, fontSize:14, color:COLOR.gold }}>→</span>
    </button>
  );
}
